import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { BuyModal } from "@/components/site/BuyModal";
import { IMG } from "@/data/content";
import { useContent } from "@/context/ContentContext";
import { findProductBySlug, slugify } from "@/lib/slug";
import { ArrowLeft, ShoppingBag, MessageCircle, Share2, Facebook, Twitter, Linkedin, Copy } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

const resolveImg = (v) =>
  v && typeof v === "string" && v.startsWith("http") ? v : IMG[v] || v;

const ProductDetail = () => {
  const { slug } = useParams();
  const { content, loading } = useContent();
  const [buyOpen, setBuyOpen] = useState(false);

  const products = content.products || [];
  const brandName = content.brand?.name || "Geomantic Kalpataru";
  const wa = `https://wa.me/${(content.brand?.whatsappNumber || "").replace(/\D/g, "")}?text=${encodeURIComponent(
    (content.brand?.whatsappMessage || "Namaste") + " (Enquiry: " + (slug || "") + ")"
  )}`;

  const product = findProductBySlug(products, slug);
  const related = products.filter((p) => slugify(p.slug || p.name) !== slug).slice(0, 3);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-ivory/60">Loading…</div>;
  }
  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center flex-col gap-4 pt-32 pb-20">
        <h1 className="font-display text-3xl text-ivory">Offering not found</h1>
        <p className="text-ivory/60">This link may have moved or been renamed.</p>
        <Link to="/products" className="btn-gold mt-3">Back to Sacred Offerings</Link>
      </div>
    );
  }

  return (
    <div data-testid="product-detail-page">
      <Toaster theme="dark" position="top-right" />
      <PageHero
        eyebrow="Sacred Offering"
        title={<>{product.name}</>}
        subtitle={product.tagline || "Hand-crafted, consecrated and tuned to the individual."}
        image={resolveImg(product.image) || IMG.crystal}
      />

      <section className="section">
        <div className="wrap grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-6">
            <div className="glass p-2 ring-gold">
              <img
                src={resolveImg(product.image) || IMG.crystal}
                alt={product.name}
                className="aspect-[4/5] w-full object-cover"
              />
            </div>
            <Link to="/products" className="mt-6 inline-flex items-center gap-2 text-gold text-xs uppercase tracking-[0.25em] hover:text-saffron transition">
              <ArrowLeft className="h-3.5 w-3.5" /> All Offerings
            </Link>
          </Reveal>

          <Reveal delay={120} className="lg:col-span-6">
            <div className="eyebrow mb-3">{product.eyebrow || "Bespoke · Personal · Consecrated"}</div>
            <h1 className="display text-4xl md:text-5xl text-ivory">{product.name}</h1>
            <div className="mt-4 font-display text-3xl text-gold-grad">
              ₹{product.priceInr || 100} <span className="text-lg text-ivory/50">· ${product.priceUsd || 2}</span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-ivory/45 mt-1">
              {product.price || "One-time · INR or USD"}
            </div>

            <div className="prose-kalpataru mt-8 text-ivory/80 leading-relaxed">
              <ReactMarkdown>{product.body || ""}</ReactMarkdown>
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setBuyOpen(true)}
                className="btn-gold"
                data-testid="detail-buy-btn"
              >
                <ShoppingBag className="h-4 w-4" /> Buy Now
              </button>
              <a
                href={wa}
                target="_blank"
                rel="noreferrer"
                className="btn-ghost"
                data-testid="detail-whatsapp-btn"
              >
                <MessageCircle className="h-4 w-4" /> Enquire on WhatsApp
              </a>
            </div>

            <div className="mt-8 pt-6 border-t border-gold/15 flex flex-wrap items-center gap-3">
              <span className="text-xs uppercase tracking-[0.25em] text-ivory/55 flex items-center gap-2">
                <Share2 className="h-3.5 w-3.5" /> Share
              </span>
              {(() => {
                const shareUrl = typeof window !== "undefined" ? window.location.href : "";
                const txt = `${product.name} — Geomantic Kalpataru`;
                const items = [
                  { Icon: Facebook, label: "facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}` },
                  { Icon: Twitter, label: "twitter", href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(txt)}` },
                  { Icon: Linkedin, label: "linkedin", href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}` },
                ];
                return (
                  <>
                    {items.map(({ Icon, label, href }) => (
                      <a key={label} href={href} target="_blank" rel="noreferrer" aria-label={label}
                         data-testid={`product-share-${label}`}
                         className="flex h-9 w-9 items-center justify-center rounded-full border border-gold/30 text-gold hover:bg-gold hover:text-midnight transition">
                        <Icon className="h-4 w-4" strokeWidth={1.5} />
                      </a>
                    ))}
                    <a href={`https://wa.me/?text=${encodeURIComponent(`${txt} — ${shareUrl}`)}`}
                       target="_blank" rel="noreferrer" data-testid="product-share-whatsapp"
                       className="text-xs uppercase tracking-[0.25em] text-gold hover:text-saffron transition">
                      WhatsApp
                    </a>
                    <a href={`https://www.threads.net/intent/post?text=${encodeURIComponent(`${txt} — via @agnapushpanjal — ${shareUrl}`)}`}
                       target="_blank" rel="noreferrer" data-testid="product-share-threads"
                       className="text-xs uppercase tracking-[0.25em] text-gold hover:text-saffron transition">
                      Threads
                    </a>
                    <button
                      type="button"
                      onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success("Link copied."); }}
                      data-testid="product-share-copy"
                      className="text-xs uppercase tracking-[0.25em] text-gold hover:text-saffron transition inline-flex items-center gap-1.5"
                    >
                      <Copy className="h-3.5 w-3.5" /> Copy Link
                    </button>
                  </>
                );
              })()}
            </div>
          </Reveal>
        </div>
      </section>

      {related.length > 0 && (
        <section className="section border-t border-gold/10 bg-midnight-paper/40">
          <div className="wrap">
            <Reveal>
              <div className="eyebrow mb-4">You may also seek</div>
              <h2 className="display text-3xl md:text-4xl text-ivory">Other sacred offerings</h2>
            </Reveal>
            <div className="mt-10 grid gap-6 md:grid-cols-3">
              {related.map((p, i) => (
                <Reveal key={p.name + i} delay={i * 60}>
                  <Link
                    to={`/products/${slugify(p.slug || p.name)}`}
                    className="block glass lift overflow-hidden group"
                    data-testid={`related-product-${i}`}
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={resolveImg(p.image) || IMG.crystal}
                        alt={p.name}
                        className="h-full w-full object-cover transition duration-700 group-hover:scale-110"
                      />
                    </div>
                    <div className="p-6">
                      <h3 className="font-display text-xl text-ivory">{p.name}</h3>
                      <div className="mt-2 text-gold">
                        ₹{p.priceInr || 100} <span className="text-ivory/40 text-sm">· ${p.priceUsd || 2}</span>
                      </div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {buyOpen && <BuyModal product={product} brandName={brandName} onClose={() => setBuyOpen(false)} />}
    </div>
  );
};

export default ProductDetail;
